/**
 * Ocean-flow gradient: an original WebGL shader in the brand mesh colors that reacts softly to the pointer.
 * Falls back to the CSS mesh when WebGL is unavailable, renders one still frame under
 * prefers-reduced-motion, and pauses when off-screen or when the tab is hidden.
 */
import { $$, prefersReducedMotion } from '../../lib/dom';
import { logger } from '../../lib/logger';
import { FLOW_FRAG, FLOW_VERT } from './flow-shaders';

type Rgb = [number, number, number];

interface FlowState {
  visible: boolean;
  mx: number;
  my: number;
  tx: number;
  ty: number;
  mi: number;
  ti: number;
  t: number;
}

interface Flow {
  readonly state: FlowState;
  draw(dt: number): void;
  dispose(): void;
}

/** The gradient is soft, so it renders at reduced resolution. */
const RENDER_SCALE = 0.4;
const MAX_PIXEL_RATIO = 1.5;
const MAX_FRAME_SECONDS = 0.05;
const STILL_FRAME_TIME = 12;
const FALLBACK_MESH_COLOR = '#B794F6';

let flows: Flow[] = [];
let animationFrame = 0;

export function hexToRgb(hex: string): Rgb {
  let h = hex.trim().replace('#', '');
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  const n = parseInt(h, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    logger.warn('Flow gradient shader failed to compile.', gl.getShaderInfoLog(shader));
    return null;
  }
  return shader;
}

function createProgram(gl: WebGLRenderingContext): WebGLProgram | null {
  const vertex = compileShader(gl, gl.VERTEX_SHADER, FLOW_VERT);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, FLOW_FRAG);
  const program = gl.createProgram();
  if (!vertex || !fragment) return null;
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  return gl.getProgramParameter(program, gl.LINK_STATUS) ? program : null;
}

function createFlow(host: HTMLElement): Flow | null {
  const section = host.parentElement;
  if (!section) return null;
  const canvas = document.createElement('canvas');
  canvas.className = 'flow-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  const gl = canvas.getContext('webgl', {
    antialias: false,
    alpha: true,
    premultipliedAlpha: false,
    powerPreference: 'low-power',
  });
  if (!gl) return null;
  const program = createProgram(gl);
  if (!program) return null;
  gl.useProgram(program);

  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, 'a');
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

  const uniform = (name: string): WebGLUniformLocation | null => gl.getUniformLocation(program, name);
  const styles = getComputedStyle(document.documentElement);
  ['c1', 'c2', 'c3', 'c4', 'c5'].forEach((name, i) => {
    gl.uniform3fv(uniform(name), hexToRgb(styles.getPropertyValue(`--m${i + 1}`) || FALLBACK_MESH_COLOR));
  });
  const uResolution = uniform('u_res');
  const uTime = uniform('u_t');
  const uMouse = uniform('u_m');
  const uMouseIntensity = uniform('u_mi');

  host.appendChild(canvas);
  host.classList.add('flow');

  const state: FlowState = { visible: true, mx: 0.7, my: 0.5, tx: 0.7, ty: 0.5, mi: 0, ti: 0, t: Math.random() * 40 };

  const draw = (dt: number): void => {
    state.t += dt;
    state.mx += (state.tx - state.mx) * 0.06;
    state.my += (state.ty - state.my) * 0.06;
    state.mi += (state.ti - state.mi) * 0.04;
    gl.uniform1f(uTime, state.t);
    gl.uniform2f(uMouse, state.mx, state.my);
    gl.uniform1f(uMouseIntensity, state.mi);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  const visibility = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      state.visible = entry.isIntersecting;
    });
  });
  visibility.observe(host);

  const onPointerMove = (event: PointerEvent): void => {
    const rect = canvas.getBoundingClientRect();
    state.tx = (event.clientX - rect.left) / rect.width;
    state.ty = 1 - (event.clientY - rect.top) / rect.height;
    state.ti = 1;
  };
  const onPointerLeave = (): void => {
    state.ti = 0;
  };
  section.addEventListener('pointermove', onPointerMove, { passive: true });
  section.addEventListener('pointerleave', onPointerLeave);

  const resize = (): void => {
    const scale = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO) * RENDER_SCALE;
    const width = Math.max(2, Math.round(canvas.clientWidth * scale));
    const height = Math.max(2, Math.round(canvas.clientHeight * scale));
    if (canvas.width === width && canvas.height === height) return;
    canvas.width = width;
    canvas.height = height;
    gl.viewport(0, 0, width, height);
    gl.uniform2f(uResolution, width, height);
    draw(0); // resizing clears the canvas: redraw immediately
  };
  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(canvas);
  resize();

  return {
    state,
    draw,
    dispose() {
      visibility.disconnect();
      resizeObserver.disconnect();
      section.removeEventListener('pointermove', onPointerMove);
      section.removeEventListener('pointerleave', onPointerLeave);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
      canvas.remove();
      host.classList.remove('flow');
    },
  };
}

function stopFlows(): void {
  cancelAnimationFrame(animationFrame);
  animationFrame = 0;
  flows.forEach((flow) => {
    flow.dispose();
  });
  flows = [];
}

export function initFlows(root: HTMLElement): void {
  stopFlows();
  flows = $$('.hero > .mesh, .cta > .mesh', root)
    .map(createFlow)
    .filter((flow): flow is Flow => flow !== null);
  if (!flows.length) return;
  if (prefersReducedMotion()) {
    flows.forEach((flow) => {
      flow.state.t = STILL_FRAME_TIME;
      flow.draw(0);
    });
    return;
  }
  let last = performance.now();
  const loop = (now: number): void => {
    const dt = Math.min(MAX_FRAME_SECONDS, (now - last) / 1000);
    last = now;
    if (!document.hidden) {
      flows.forEach((flow) => {
        if (flow.state.visible) flow.draw(dt);
      });
    }
    animationFrame = requestAnimationFrame(loop);
  };
  animationFrame = requestAnimationFrame(loop);
}
